<template>
  <div class="mp-chat">
    <!-- Header -->
    <div class="mp-chat__header">
      <div class="mp-chat__title">
        <span v-if="chat">{{ chat.name }}</span>
        <span v-else class="mp-chat__placeholder-title">Angel IA</span>
      </div>
      <div class="mp-chat__header-meta" v-if="chat">
        <button
          v-if="messages.length"
          class="mp-action-btn mp-header-copy-btn"
          :title="copiedChat ? 'Copié !' : 'Copier tout le chat'"
          @click="copyChat"
        >
          <svg v-if="!copiedChat" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
          </svg>
          <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#34c759" stroke-width="2.5">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </button>
        <select
          class="mp-header-select"
          :value="currentLlmId"
          :disabled="isStreaming || !llms.length"
          @change="onLlmSelectChange"
        >
          <option value="">Modèle…</option>
          <option v-for="l in llms" :key="l.id" :value="l.id">{{ l.title || l.name }}</option>
        </select>
        <select
          class="mp-header-select"
          :value="currentEstUid"
          :disabled="!establishments.length"
          @change="onEstSelectChange"
        >
          <option value="">Établissement…</option>
          <option v-for="e in establishments" :key="e.uid" :value="e.uid">{{ e.name }}</option>
        </select>
      </div>
    </div>

    <!-- Messages -->
    <div ref="messages" class="mp-chat__messages">
      <div v-if="!chat" class="mp-empty-state">
        <div class="mp-empty-state__icon">✦</div>
        <h2 class="mp-empty-state__title">Angel IA</h2>
        <p class="mp-empty-state__sub">Sélectionnez un chat ou créez-en un nouveau</p>
      </div>

      <div v-else-if="!messages.length && !isStreaming && !isLoadingMessages" class="mp-empty-state">
        <div class="mp-empty-state__icon">✦</div>
        <h2 class="mp-empty-state__title">Nouveau chat</h2>
        <p class="mp-empty-state__sub">Envoyez un message pour commencer</p>
      </div>

      <div v-if="isLoadingMessages" class="mp-loading">
        <div class="mp-dot-loader">
          <span></span><span></span><span></span>
        </div>
      </div>

      <template v-for="(msg, i) in messages">
        <div
          :key="i"
          class="mp-message"
          :class="msg.sender === 'user' ? 'mp-message--user' : 'mp-message--assistant'"
        >
          <div class="mp-message__col">
            <div class="mp-message__author">
              <span>{{ msg.sender === 'user' ? 'Vous' : aiName }}</span>
              <span v-if="msg.created_at" class="mp-message__time">{{ formatTime(msg.created_at) }}</span>
            </div>
            <div class="mp-message__bubble">
              <div class="mp-message__text" v-if="msg.sender === 'assistant'" v-html="renderText(msg.text)"></div>
              <div class="mp-message__text" v-else v-html="renderUserText(msg.text)"></div>
              <!-- Pièces jointes référencées dans le message utilisateur -->
              <div v-if="msg.attachmentNames && msg.attachmentNames.length" class="mp-message__attach-list">
                <span v-for="(name, j) in msg.attachmentNames" :key="j" class="mp-message__attach-chip">
                  📎 {{ name }}
                </span>
              </div>
            </div>
            <div v-if="msg.sender === 'assistant'" class="mp-message__actions">
              <button
                class="mp-action-btn"
                :title="copiedMsgId === msg.id ? 'Copié !' : 'Copier'"
                @click="copyMessage(msg)"
              >
                <svg v-if="copiedMsgId !== msg.id" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                </svg>
                <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#34c759" stroke-width="2.5">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </template>

      <!-- Outils actifs (web_search, fetch_url, …) -->
      <div v-if="isStreaming && toolLabels.length" class="mp-tool-status">
        <div v-for="(tool, i) in toolLabels" :key="i" class="mp-tool-status__item">
          <span class="mp-search-spinner"></span>
          <span class="mp-tool-status__icon">{{ tool.icon }}</span>
          <span class="mp-tool-status__text">{{ tool.text }}</span>
        </div>
      </div>

      <!-- Streaming message -->
      <div v-if="isStreaming" class="mp-message mp-message--assistant">
        <div class="mp-message__bubble mp-message__bubble--streaming">
          <div class="mp-message__text" v-if="streamingText" v-html="renderText(streamingText)"></div>
          <div v-else class="mp-dot-loader mp-dot-loader--dark">
            <span></span><span></span><span></span>
          </div>
        </div>
      </div>
    </div>

    <!-- Input -->
    <div
      class="mp-chat__input-area"
      :class="{ 'mp-chat__input-area--dragover': isDragging }"
      @dragenter="onDragEnter"
      @dragover="onDragOver"
      @dragleave="onDragLeave"
      @drop="onDrop"
    >
      <div v-if="isDragging" class="mp-drop-overlay">
        <div class="mp-drop-overlay__label">📎 Déposez vos fichiers ici</div>
      </div>
      <!-- Fichiers en cours d'upload -->
      <div v-if="pendingFiles.length" class="mp-pending-files">
        <span v-for="f in pendingFiles" :key="f.uid" class="mp-pending-chip" :class="{ 'mp-pending-chip--error': f.error }">
          <span v-if="f.uploading" class="mp-pending-spinner"></span>
          <span v-else-if="f.error">⚠️</span>
          <span v-else>{{ fileIcon(f.type) }}</span>
          {{ f.name }}
          <button v-if="!f.uploading" class="mp-pending-remove" @click="removePending(f.uid)">×</button>
        </span>
      </div>
      <!-- Pièces jointes prêtes à envoyer -->
      <div v-if="attachments.length" class="mp-pending-files">
        <span v-for="(att, i) in attachments" :key="'att-' + i" class="mp-pending-chip mp-pending-chip--ready">
          {{ att.type === 'image' ? '🖼️' : fileIcon(att.content_type) }} {{ att.filename }}
          <button class="mp-pending-remove" @click="removeAttachment(i)">×</button>
        </span>
      </div>

      <!-- Tag établissement lié -->
      <div v-if="chat && chat.establishment_name" class="mp-est-bar">
        <span class="mp-est-bar__icon">🏨</span>
        <span class="mp-est-bar__name">{{ chat.establishment_name }}</span>
        <button class="mp-est-bar__remove" title="Délier l'établissement" @click="$emit('clear-establishment')">×</button>
      </div>

      <!-- LLM sélectionné via @ -->
      <div v-if="selectedLlmId" class="mp-pending-files">
        <span class="mp-pending-chip mp-pending-chip--llm">
          ✦ {{ selectedLlmName }}
          <button class="mp-pending-remove" @click="clearSelectedLlm">×</button>
        </span>
      </div>
      <!-- Dropdown @LLM -->
      <div v-if="llmQuery !== null && filteredLlms.length" class="mp-llm-dropdown">
        <button
          v-for="(llm, idx) in filteredLlms"
          :key="llm.id"
          class="mp-llm-option"
          :class="{ 'mp-llm-option--active': idx === llmDropdownIndex }"
          @mousedown.prevent="selectLlm(llm)"
        >
          <span class="mp-llm-option__title">{{ llm.title || llm.name }}</span>
          <span class="mp-llm-option__provider">{{ llm.litellm_provider }}</span>
        </button>
      </div>
      <!-- Dropdown #Établissement -->
      <div v-if="estQuery !== null && filteredEsts.length" class="mp-llm-dropdown">
        <button
          v-for="(est, idx) in filteredEsts"
          :key="est.uid"
          class="mp-llm-option"
          :class="{ 'mp-llm-option--active': idx === estDropdownIndex }"
          @mousedown.prevent="selectEst(est)"
        >
          <span class="mp-llm-option__title">{{ est.name }}</span>
          <span class="mp-llm-option__provider">Établissement</span>
        </button>
      </div>

      <!-- Formulaire génération image désactivé
      <transition name="mp-fade">
        <div v-if="showImageForm" class="mp-image-form">
          <textarea
            v-model="imagePrompt"
            class="mp-image-prompt"
            placeholder="Décrivez l'image à générer…"
            rows="2"
            @keydown.esc="showImageForm = false"
          ></textarea>
          <div class="mp-image-form__actions">
            <button class="mp-image-cancel" @click="showImageForm = false">Annuler</button>
            <button
              class="mp-image-generate"
              :disabled="!imagePrompt.trim() || isStreaming"
              @click="submitImageGeneration"
            >Générer</button>
          </div>
        </div>
      </transition>
      -->

      <div class="mp-input-box" :class="{ 'mp-input-box--disabled': !chat || isStreaming }">
        <!-- @mention dropdown désactivé
        <div
          v-if="mentionQuery !== null && mentionItems.length"
          class="mp-mention-dropdown"
          :class="{ 'mp-mention-dropdown--below': mentionDropdownPos === 'below' }"
        >
          ...
        </div>
        -->

        <input
          ref="fileInput"
          type="file"
          multiple
          accept=".txt,.md,.csv,.xlsx,.pdf,.docx,image/*"
          style="display:none"
          @change="onFileChange"
        />
        <button
          class="mp-attach-btn"
          title="Joindre un fichier"
          :disabled="!chat || isStreaming"
          @click="openFilePicker"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
          </svg>
        </button>
        <!-- Bouton génération image désactivé
        <button
          v-if="supportsImageGeneration"
          class="mp-attach-btn"
          title="Générer une image"
          :disabled="!chat || isStreaming"
          @click="showImageForm = !showImageForm"
        >
          ...
        </button>
        -->

        <div
          ref="editor"
          class="mp-editor"
          :contenteditable="chat && !isStreaming ? 'true' : 'false'"
          data-placeholder="Écrivez un message…"
          @keydown="onEditorKeydown"
          @input="onEditorInput"
          @paste="onEditorPaste"
        ></div>

        <button
          class="mp-send-btn"
          :class="{ 'mp-send-btn--active': inputText.trim() && !isAnyUploading && !isStreaming }"
          :disabled="!inputText.trim() || isAnyUploading || isStreaming || !chat"
          @click="submit"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <line x1="22" y1="2" x2="11" y2="13"/>
            <polygon points="22 2 15 22 11 13 2 9 22 2"/>
          </svg>
        </button>
      </div>
      <div class="mp-input-hint">Entrée pour envoyer · Maj+Entrée pour nouvelle ligne</div>
    </div>
  </div>
</template>

<script>
// import MpKbDropdown from './MpKbDropdown.vue';
import settings from '@/settings.coffee';


export default {
  name: 'MpChat',
  // components: { MpKbDropdown },
  props: {
    chat: { type: Object, default: null },
    // activeKb: { type: Object, default: null },
    // knowledgeBases: { type: Array, default: () => [] },
    // establishments: { type: Array, default: () => [] },
    // establishmentUid: { type: String, default: null },
    messages: { type: Array, default: () => [] },
    isStreaming: { type: Boolean, default: false },
    streamingText: { type: String, default: '' },
    activeTools: { type: Array, default: function() { return []; } },
    isLoadingMessages: { type: Boolean, default: false },
    llms: { type: Array, default: function() { return []; } },
    establishments: { type: Array, default: function() { return []; } },
  },
  data() {
    return {
      inputText: '',
      pendingFiles: [],
      attachments: [],
      copiedMsgId: null,
      copiedChat: false,
      llmQuery: null,
      llmDropdownIndex: 0,
      selectedLlmId: null,
      selectedLlmName: null,
      estQuery: null,
      estDropdownIndex: 0,
      isDragging: false,
      dragCounter: 0,
    };
  },
  mounted() {
    document.addEventListener('click', this.closeModelPicker);
    // document.addEventListener('click', this.closeMentionDropdown);
    // document.addEventListener('click', this.closeFileList);
    document.addEventListener('keydown', this.handleEsc);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeModelPicker);
    // document.removeEventListener('click', this.closeMentionDropdown);
    // document.removeEventListener('click', this.closeFileList);
    document.removeEventListener('keydown', this.handleEsc);
  },
  computed: {
    currentLlmId() {
      if (!this.chat || !this.llms.length) return '';
      var match = this.llms.find(function(l) { return l.name === this.chat.llm; }.bind(this));
      return match ? match.id : '';
    },
    currentEstUid() {
      return (this.chat && this.chat.establishment_uid) || '';
    },
    filteredLlms() {
      if (this.llmQuery === null) return [];
      var q = this.llmQuery.toLowerCase();
      return this.llms.filter(function(l) {
        var label = (l.title || l.name).toLowerCase();
        return !q || label.includes(q) || l.name.toLowerCase().includes(q);
      });
    },
    filteredEsts() {
      if (this.estQuery === null) return [];
      var q = this.estQuery.toLowerCase();
      return this.establishments.filter(function(e) {
        return !q || e.name.toLowerCase().includes(q);
      });
    },
    isAnyUploading() {
      return this.pendingFiles.some(function(f) { return f.uploading; });
    },
    aiName() {
      if (!this.chat) return 'IA';
      return this.chat.llm_model_title || this.chat.llm || 'IA';
    },
    // supportsImageGeneration() { ... },
    // groupedLlms() { ... },

    // Libellés lisibles pour chaque outil actif
    toolLabels() {
      return this.activeTools.map(function(t) {
        if (t.tool === 'web_search') return { icon: '🔍', text: t.query ? ('Recherche : ' + t.query) : 'Recherche web…' };
        if (t.tool === 'fetch_url')  return { icon: '🌐', text: t.url   ? ('Lecture : '   + t.url)   : 'Lecture de la page…' };
        return { icon: '⚙️', text: t.tool };
      });
    },
  },
  watch: {
    messages() {
      this.$nextTick(() => this.scrollToBottom());
    },
    streamingText() {
      this.$nextTick(() => this.scrollToBottom());
    },
    'chat.id'() {
      var editor = this.$refs.editor;
      if (editor) editor.innerHTML = '';
      this.inputText = '';
      this.pendingFiles = [];
      this.attachments = [];
      this.llmQuery = null;
      this.estQuery = null;
      this.selectedLlmId = null;
      this.selectedLlmName = null;
      var self = this;
      this.$nextTick(function() { if (self.$refs.editor) self.$refs.editor.focus(); });
    },
    isStreaming(val) {
      if (!val) {
        var self = this;
        this.$nextTick(function() { if (self.$refs.editor) self.$refs.editor.focus(); });
      }
    },
    // mentionIndex(idx) { ... },
  },
  methods: {
    /* ── Editor input handling ── */
    onEditorInput() {
      var editor = this.$refs.editor;
      if (editor && editor.childNodes.length === 1 && editor.firstChild.nodeName === 'BR') {
        editor.innerHTML = '';
      }
      this.inputText = this.getEditorText();
      this.detectLlmMention();
    },
    onEditorKeydown(e) {
      if (this.llmQuery !== null && this.filteredLlms.length) {
        if (e.key === 'ArrowDown') { e.preventDefault(); this.llmDropdownIndex = (this.llmDropdownIndex + 1) % this.filteredLlms.length; return; }
        if (e.key === 'ArrowUp')   { e.preventDefault(); this.llmDropdownIndex = (this.llmDropdownIndex - 1 + this.filteredLlms.length) % this.filteredLlms.length; return; }
        if (e.key === 'Enter')     { e.preventDefault(); this.selectLlm(this.filteredLlms[this.llmDropdownIndex]); return; }
        if (e.key === 'Escape')    { this.llmQuery = null; return; }
      }
      if (this.estQuery !== null && this.filteredEsts.length) {
        if (e.key === 'ArrowDown') { e.preventDefault(); this.estDropdownIndex = (this.estDropdownIndex + 1) % this.filteredEsts.length; return; }
        if (e.key === 'ArrowUp')   { e.preventDefault(); this.estDropdownIndex = (this.estDropdownIndex - 1 + this.filteredEsts.length) % this.filteredEsts.length; return; }
        if (e.key === 'Enter')     { e.preventDefault(); this.selectEst(this.filteredEsts[this.estDropdownIndex]); return; }
        if (e.key === 'Escape')    { this.estQuery = null; return; }
      }
      if (e.key === 'Enter' && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        this.submit();
      } else if (e.key === 'Enter' && e.shiftKey) {
        e.preventDefault();
        document.execCommand('insertLineBreak');
      }
    },
    onEditorPaste(e) {
      e.preventDefault();
      var text = (e.clipboardData || window.clipboardData).getData('text/plain');
      if (text) document.execCommand('insertText', false, text);
    },

    /* ── @LLM mention ── */
    detectLlmMention() {
      var text = this.inputText;
      if (text.startsWith('@')) {
        this.llmQuery = text.slice(1).toLowerCase();
        this.llmDropdownIndex = 0;
        this.estQuery = null;
      } else if (text.startsWith('#')) {
        this.estQuery = text.slice(1).toLowerCase();
        this.estDropdownIndex = 0;
        this.llmQuery = null;
      } else {
        this.llmQuery = null;
        this.estQuery = null;
      }
    },
    selectLlm(llm) {
      this.selectedLlmId = parseInt(llm.id, 10);
      this.selectedLlmName = llm.title || llm.name;
      this.llmQuery = null;
      var editor = this.$refs.editor;
      if (editor) editor.innerHTML = '';
      this.inputText = '';
      var self = this;
      this.$nextTick(function() { if (self.$refs.editor) self.$refs.editor.focus(); });
    },
    clearSelectedLlm() {
      this.selectedLlmId = null;
      this.selectedLlmName = null;
    },
    onLlmSelectChange(e) {
      var id = parseInt(e.target.value, 10);
      if (!id) return;
      this.$emit('change-llm', { llmId: id });
    },
    onEstSelectChange(e) {
      var uid = e.target.value;
      if (!uid) {
        this.$emit('clear-establishment');
      } else {
        this.$emit('link-establishment', { uid: uid });
      }
    },
    selectEst(est) {
      this.estQuery = null;
      var editor = this.$refs.editor;
      if (editor) editor.innerHTML = '';
      this.inputText = '';
      var self = this;
      this.$nextTick(function() { if (self.$refs.editor) self.$refs.editor.focus(); });
      this.$emit('link-establishment', { uid: est.uid });
    },

    /* ── Editor serialization ── */
    getEditorText() {
      var editor = this.$refs.editor;
      if (!editor) return '';
      function extract(node) {
        if (node.nodeType === Node.TEXT_NODE) {
          return node.textContent.replace(/[​ ]/g, ' ');
        }
        if (node.nodeName === 'BR') return '\n';
        var inner = Array.from(node.childNodes).map(extract).join('');
        if ((node.nodeName === 'DIV' || node.nodeName === 'P') && inner) return '\n' + inner;
        return inner;
      }
      return Array.from(editor.childNodes).map(extract).join('').trim();
    },

    /* ── Submit ── */
    submit() {
      var text = this.getEditorText();
      if (!text.trim() || this.isStreaming || !this.chat || this.isAnyUploading) return;
      var editor = this.$refs.editor;
      if (editor) editor.innerHTML = '';
      this.inputText = '';
      var attachments = this.attachments.slice();
      this.attachments = [];
      var llmId = this.selectedLlmId;
      this.selectedLlmId = null;
      this.selectedLlmName = null;
      this.$emit('send', { text: text, attachments: attachments, llmId: llmId });
    },

    /* ── Fichiers ── */
    openFilePicker() {
      this.$refs.fileInput.click();
    },
    onFileChange(e) {
      var files = Array.from(e.target.files);
      e.target.value = '';
      this.handleFiles(files);
    },
    handleFiles(files) {
      if (!files || !files.length) return;
      var self = this;
      files.forEach(function(file) {
        var uid = file.name + '-' + file.size + '-' + file.lastModified;
        self.pendingFiles.push({ uid: uid, name: file.name, type: file.type, uploading: true, error: false });
        self.uploadFile(file, uid);
      });
    },
    onDragEnter(e) {
      if (!this.chat || this.isStreaming) return;
      e.preventDefault();
      this.dragCounter++;
      this.isDragging = true;
    },
    onDragOver(e) {
      if (!this.chat || this.isStreaming) return;
      e.preventDefault();
    },
    onDragLeave(e) {
      if (!this.chat || this.isStreaming) return;
      e.preventDefault();
      this.dragCounter--;
      if (this.dragCounter <= 0) {
        this.dragCounter = 0;
        this.isDragging = false;
      }
    },
    onDrop(e) {
      if (!this.chat || this.isStreaming) return;
      e.preventDefault();
      this.dragCounter = 0;
      this.isDragging = false;
      var files = Array.from(e.dataTransfer.files);
      this.handleFiles(files);
    },
    async uploadFile(file, uid) {
      var formData = new FormData();
      formData.append('file', file);
      var csrfToken = this.getCsrfToken();
      try {
        var response = await fetch(settings.backendUrl.replace(/\/$/, '') + '/admin/my-prompt/upload', {
          method: 'POST',
          body: formData,
          headers: csrfToken ? { 'X-CSRFToken': csrfToken } : {},
          credentials: 'include',
        });
        if (!response.ok) throw new Error('Upload failed');
        var result = await response.json();
        var idx = this.pendingFiles.findIndex(function(p) { return p.uid === uid; });
        if (idx !== -1) this.pendingFiles.splice(idx, 1);
        this.attachments.push(result);
      } catch (err) {
        this.markUploadError(uid);
      }
    },
    getCsrfToken() {
      var match = document.cookie.match(/csrftoken=([^;]+)/);
      return match ? match[1] : '';
    },
    markUploadError(uid) {
      var idx = this.pendingFiles.findIndex(function(p) { return p.uid === uid; });
      if (idx !== -1) this.$set(this.pendingFiles, idx, Object.assign({}, this.pendingFiles[idx], { uploading: false, error: true }));
    },
    removePending(uid) {
      var idx = this.pendingFiles.findIndex(function(p) { return p.uid === uid; });
      if (idx !== -1) this.pendingFiles.splice(idx, 1);
    },
    removeAttachment(i) {
      this.attachments.splice(i, 1);
    },
    fileIcon(contentType) {
      if (!contentType) return '📄';
      if (contentType.startsWith('image/')) return '🖼️';
      if (contentType.includes('pdf')) return '📕';
      if (contentType.includes('word') || contentType.includes('docx')) return '📝';
      if (contentType.includes('sheet') || contentType.includes('xlsx') || contentType.includes('csv')) return '📊';
      return '📄';
    },

    /* ── Formatage ── */
    formatTime(dateStr) {
      var d = new Date(dateStr);
      if (isNaN(d.getTime())) return '';
      var now = new Date();
      var sameDay = d.toDateString() === now.toDateString();
      var time = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
      if (sameDay) return time;
      var date = d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
      return date + ' ' + time;
    },

    /* ── Scroll ── */
    scrollToBottom() {
      var el = this.$refs.messages;
      if (el) el.scrollTop = el.scrollHeight;
    },

    /* ── Model picker désactivé ──
    selectModel(m) { ... },
    */
    closeModelPicker() {
      // désactivé — conservé pour l'écouteur d'événement
    },
    handleEsc(e) {
      if (e.key === 'Escape') {
        this.llmQuery = null;
        this.estQuery = null;
      }
    },

    /* ── Message actions ── */
    copyMessage(msg) {
      var text = msg.text || '';
      var self = this;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(function() {
          self.copiedMsgId = msg.id;
          setTimeout(function() { self.copiedMsgId = null; }, 2000);
        }).catch(function() { self._fallbackCopy(text); });
      } else {
        this._fallbackCopy(text);
      }
    },
    copyChat() {
      var self = this;
      var text = this.messages.map(function(m) {
        var author = m.sender === 'user' ? 'Vous' : self.aiName;
        return author + ' :\n' + (m.text || '');
      }).join('\n\n---\n\n');
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(function() {
          self.copiedChat = true;
          setTimeout(function() { self.copiedChat = false; }, 2000);
        }).catch(function() { self._fallbackCopy(text); });
      } else {
        this._fallbackCopy(text);
      }
    },
    _fallbackCopy(text) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      this.$store.dispatch('notify', { level: 'success', text: 'Copié !', seconds: 2 });
    },

    /* ── Image / mail désactivés ──
    submitImageGeneration() { ... },
    mailMessage(msg) { ... },
    regenImage(msg) { ... },
    closeFileList(e) { ... },
    imageUrlFromMessage(msg) { ... },
    */

    renderUserText(text) {
      if (!text) return '';
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>');
    },
    renderText(text) {
      if (!text) return '';
      var escaped = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      var lines = escaped.split('\n');
      var html = '';
      var i = 0;
      while (i < lines.length) {
        var line = lines[i];
        // Bloc de code ```
        if (line.match(/^```/)) {
          var lang = line.slice(3).trim();
          var code = '';
          i++;
          while (i < lines.length && !lines[i].match(/^```/)) {
            code += lines[i] + '\n';
            i++;
          }
          html += '<pre><code' + (lang ? ' class="language-' + lang + '"' : '') + '>' + code + '</code></pre>';
          i++;
          continue;
        }
        // Titres
        var hm = line.match(/^(#{1,4})\s+(.+)/);
        if (hm) {
          var lvl = hm[1].length;
          html += '<h' + lvl + '>' + this._inlineMd(hm[2]) + '</h' + lvl + '>';
          i++;
          continue;
        }
        // Séparateur
        if (line.match(/^(-{3,}|\*{3,}|_{3,})$/)) {
          html += '<hr>';
          i++;
          continue;
        }
        // Blockquote
        if (line.match(/^&gt;\s?/)) {
          var bq = '';
          while (i < lines.length && lines[i].match(/^&gt;\s?/)) {
            bq += lines[i].replace(/^&gt;\s?/, '') + ' ';
            i++;
          }
          html += '<blockquote>' + this._inlineMd(bq.trim()) + '</blockquote>';
          continue;
        }
        // Liste non-ordonnée
        if (line.match(/^[-*+]\s+/)) {
          html += '<ul>';
          while (i < lines.length && lines[i].match(/^[-*+]\s+/)) {
            html += '<li>' + this._inlineMd(lines[i].replace(/^[-*+]\s+/, '')) + '</li>';
            i++;
          }
          html += '</ul>';
          continue;
        }
        // Liste ordonnée
        if (line.match(/^\d+\.\s+/)) {
          html += '<ol>';
          while (i < lines.length && lines[i].match(/^\d+\.\s+/)) {
            html += '<li>' + this._inlineMd(lines[i].replace(/^\d+\.\s+/, '')) + '</li>';
            i++;
          }
          html += '</ol>';
          continue;
        }
        // Tableau GFM
        if (i + 1 < lines.length && lines[i + 1].match(/^\|?[\s-:|]+\|/)) {
          var header = line.split('|').filter(function(c, idx, arr) { return idx > 0 || c.trim(); }).map(function(c) { return c.trim(); });
          i += 2;
          html += '<table><thead><tr>';
          header.forEach(function(c) { html += '<th>' + this._inlineMd(c) + '</th>'; }.bind(this));
          html += '</tr></thead><tbody>';
          while (i < lines.length && lines[i].match(/\|/)) {
            var row = lines[i].split('|').filter(function(c, idx, arr) { return idx > 0 || c.trim(); }).map(function(c) { return c.trim(); });
            html += '<tr>';
            row.forEach(function(c) { html += '<td>' + this._inlineMd(c) + '</td>'; }.bind(this));
            html += '</tr>';
            i++;
          }
          html += '</tbody></table>';
          continue;
        }
        // Ligne vide
        if (!line.trim()) {
          i++;
          continue;
        }
        // Paragraphe
        var para = '';
        while (i < lines.length && lines[i].trim() && !lines[i].match(/^(#{1,4}\s|```|&gt;|[-*+]\s|\d+\.\s|-{3,}|\*{3,})/)) {
          para += (para ? ' ' : '') + lines[i];
          i++;
        }
        if (para) html += '<p>' + this._inlineMd(para) + '</p>';
      }
      return html;
    },
    _inlineMd(text) {
      if (!text) return '';
      return text
        .replace(/`([^`]+)`/g, '<code>$1</code>')
        .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/~~(.+?)~~/g, '<del>$1</del>')
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
    },
  },
};
</script>

<style scoped>
.mp-chat {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: #fafaf9;
  height: 100%;
  overflow: hidden;
}

/* Header */
.mp-chat__header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 20px;
  height: 52px;
  border-bottom: 1px solid #e8e8e6;
  background: #fafaf9;
  flex-shrink: 0;
}
.mp-chat__title {
  font-size: 14px;
  font-weight: 600;
  color: #1c1c1e;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.mp-chat__placeholder-title { color: #8e8e93; }
.mp-chat__header-meta {
  display: flex;
  gap: 8px;
  align-items: center;
}
.mp-header-select {
  font-size: 12px;
  font-family: inherit;
  color: #3a3a3c;
  background: #f0f0ee;
  border: 1.5px solid #e8e8e6;
  border-radius: 8px;
  padding: 4px 26px 4px 10px;
  height: 30px;
  cursor: pointer;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23aeaeb2' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;
  max-width: 180px;
  transition: border-color 0.15s;
}
.mp-header-select:focus { border-color: rgb(124, 156, 156); }
.mp-header-select:disabled { opacity: 0.4; cursor: default; }
.mp-tag {
  font-size: 11px;
  background: #f0f0ee;
  color: #636366;
  padding: 2px 8px;
  border-radius: 100px;
  font-weight: 500;
}

/* Messages */
.mp-chat__messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px 0;
  scroll-behavior: smooth;
}
.mp-chat__messages::-webkit-scrollbar { width: 5px; }
.mp-chat__messages::-webkit-scrollbar-track { background: transparent; }
.mp-chat__messages::-webkit-scrollbar-thumb { background: #d1d1d6; border-radius: 3px; }

/* Empty state */
.mp-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 300px;
  text-align: center;
  padding: 40px;
  color: #8e8e93;
}
.mp-empty-state__icon { font-size: 32px; margin-bottom: 16px; color: #c7c7cc; }
.mp-empty-state__title { font-size: 20px; font-weight: 600; color: #3a3a3c; margin: 0 0 8px; }
.mp-empty-state__sub { font-size: 14px; color: #8e8e93; margin: 0; }

/* Loading */
.mp-loading { display: flex; justify-content: center; padding: 32px; }
.mp-dot-loader { display: flex; gap: 5px; align-items: center; }
.mp-dot-loader span {
  width: 6px; height: 6px;
  background: #c7c7cc;
  border-radius: 50%;
  animation: mp-bounce 1.2s ease-in-out infinite;
}
.mp-dot-loader span:nth-child(2) { animation-delay: 0.2s; }
.mp-dot-loader span:nth-child(3) { animation-delay: 0.4s; }
.mp-dot-loader--dark span { background: #8e8e93; }
@keyframes mp-bounce {
  0%, 80%, 100% { transform: scale(0.7); opacity: 0.5; }
  40% { transform: scale(1); opacity: 1; }
}

/* Messages */
.mp-message {
  padding: 4px 24px;
  display: flex;
  animation: mp-fade-in 0.2s ease;
}
@keyframes mp-fade-in {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}
.mp-message--user { justify-content: flex-end; }
.mp-message--assistant { justify-content: flex-start; }

.mp-message__col {
  display: flex;
  flex-direction: column;
  max-width: 680px;
}
.mp-message--user .mp-message__col { align-items: flex-end; }
.mp-message--assistant .mp-message__col { align-items: flex-start; }

.mp-message__author {
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #aeaeb2;
  margin-bottom: 3px;
  padding: 0 4px;
}
.mp-message__time {
  font-weight: 400;
  color: #c7c7cc;
}

.mp-message__bubble {
  padding: 10px 14px;
  border-radius: 14px;
  line-height: 1.6;
  font-size: 14px;
}
.mp-message--user .mp-message__bubble {
  background: var(--bs-primary, #7c9c9c);
  color: #ffffff;
  border-radius: 14px 14px 4px 14px;
}
.mp-message--assistant .mp-message__bubble {
  background: #ffffff;
  color: #1c1c1e;
  border: 1px solid #e8e8e6;
  border-radius: 14px 14px 14px 4px;
}
.mp-message__bubble--streaming { border-color: #d1d1d6; }
.mp-message__text { word-break: break-word; line-height: 1.65; }

/* Markdown — bulles assistant */
.mp-message--assistant .mp-message__text :deep(p) { margin: 0 0 10px; }
.mp-message--assistant .mp-message__text :deep(p:last-child) { margin-bottom: 0; }
.mp-message--assistant .mp-message__text :deep(h1),
.mp-message--assistant .mp-message__text :deep(h2),
.mp-message--assistant .mp-message__text :deep(h3),
.mp-message--assistant .mp-message__text :deep(h4) {
  font-weight: 700;
  line-height: 1.3;
  margin: 14px 0 6px;
  color: #1c1c1e;
}
.mp-message--assistant .mp-message__text :deep(h1) { font-size: 17px; }
.mp-message--assistant .mp-message__text :deep(h2) { font-size: 15px; }
.mp-message--assistant .mp-message__text :deep(h3) { font-size: 14px; }
.mp-message--assistant .mp-message__text :deep(ul),
.mp-message--assistant .mp-message__text :deep(ol) {
  padding-left: 20px;
  margin: 6px 0 10px;
}
.mp-message--assistant .mp-message__text :deep(li) { margin-bottom: 3px; }
.mp-message--assistant .mp-message__text :deep(li > p) { margin: 0; }
.mp-message--assistant .mp-message__text :deep(blockquote) {
  border-left: 3px solid #d1d1d6;
  margin: 8px 0;
  padding: 4px 12px;
  color: #636366;
  font-style: italic;
}
.mp-message--assistant .mp-message__text :deep(hr) {
  border: none;
  border-top: 1px solid #e8e8e6;
  margin: 12px 0;
}
.mp-message--assistant .mp-message__text :deep(table) {
  border-collapse: collapse;
  width: 100%;
  margin: 8px 0;
  font-size: 13px;
}
.mp-message--assistant .mp-message__text :deep(th),
.mp-message--assistant .mp-message__text :deep(td) {
  border: 1px solid #e8e8e6;
  padding: 6px 10px;
  text-align: left;
}
.mp-message--assistant .mp-message__text :deep(th) {
  background: #f5f5f3;
  font-weight: 600;
}
.mp-message--assistant .mp-message__text :deep(a) {
  color: rgb(124, 156, 156);
  text-decoration: underline;
}

/* Code inline */
.mp-message__text :deep(code) {
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 12px;
  background: #f0f0ee;
  padding: 1px 5px;
  border-radius: 4px;
}
.mp-message--user .mp-message__text :deep(code) { background: rgba(255,255,255,0.2); }

/* Blocs de code */
.mp-message--assistant .mp-message__text :deep(pre) {
  background: #1c1c1e;
  border-radius: 8px;
  padding: 12px 14px;
  margin: 8px 0;
  overflow-x: auto;
}
.mp-message--assistant .mp-message__text :deep(pre code) {
  background: none;
  padding: 0;
  font-size: 12.5px;
  color: #e2e8f0;
  border-radius: 0;
}

/* Pièces jointes dans les bulles */
.mp-message__attach-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}
.mp-message__attach-chip {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 6px;
  background: rgba(255,255,255,0.2);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

/* Input area */
.mp-chat__input-area {
  position: relative;
  padding: 12px 24px 16px;
  border-top: 1px solid #e8e8e6;
  background: #fafaf9;
  flex-shrink: 0;
}
.mp-chat__input-area--dragover .mp-input-box { border-color: rgb(124, 156, 156); }
.mp-drop-overlay {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(124, 156, 156, 0.08);
  border: 2px dashed rgb(124, 156, 156);
  border-radius: 12px;
  margin: 6px;
  pointer-events: none;
}
.mp-drop-overlay__label {
  font-size: 13px;
  font-weight: 600;
  color: rgb(124, 156, 156);
  background: #ffffff;
  padding: 6px 14px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}
.mp-input-box {
  position: relative;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  background: #ffffff;
  border: 1.5px solid #e8e8e6;
  border-radius: 12px;
  padding: 8px 8px 8px 14px;
  transition: border-color 0.15s;
}
.mp-input-box:focus-within { border-color: rgb(124, 156, 156); }
.mp-input-box--disabled { opacity: 0.5; }

/* Contenteditable editor */
.mp-editor {
  flex: 1;
  min-height: 22px;
  max-height: 160px;
  overflow-y: auto;
  font-size: 14px;
  color: #1c1c1e;
  line-height: 1.5;
  font-family: inherit;
  outline: none;
  word-break: break-word;
  padding: 0;
}
.mp-editor:empty::before {
  content: attr(data-placeholder);
  color: #aeaeb2;
  pointer-events: none;
}
.mp-editor[contenteditable="false"] { cursor: default; }

.mp-send-btn {
  background: #e8e8e6;
  border: none;
  color: #aeaeb2;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s;
}
.mp-send-btn--active { background: rgb(124, 156, 156); color: #ffffff; }
.mp-send-btn:disabled { cursor: default; }

.mp-input-hint {
  font-size: 11px;
  color: #c7c7cc;
  text-align: center;
  margin-top: 7px;
}

/* Message actions */
.mp-message__actions {
  display: flex;
  gap: 4px;
  margin-top: 4px;
  padding-left: 2px;
  opacity: 0;
  transition: opacity 0.15s;
}
.mp-message:hover .mp-message__actions { opacity: 1; }
.mp-action-btn {
  background: none;
  border: none;
  color: #aeaeb2;
  cursor: pointer;
  padding: 4px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  transition: color 0.12s, background 0.12s;
}
.mp-action-btn:hover { color: #636366; background: #f0f0ee; }

/* Indicateurs d'outils actifs */
.mp-tool-status {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px 24px;
}
.mp-tool-status__item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #8e8e93;
  font-style: italic;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.mp-tool-status__icon { flex-shrink: 0; font-style: normal; }
.mp-tool-status__text { overflow: hidden; text-overflow: ellipsis; }
.mp-search-spinner {
  display: inline-block;
  width: 10px;
  height: 10px;
  border: 1.5px solid #d1d1d6;
  border-top-color: #636366;
  border-radius: 50%;
  animation: mp-spin 0.7s linear infinite;
  flex-shrink: 0;
}
@keyframes mp-spin { to { transform: rotate(360deg); } }

/* Fichiers en attente / pièces jointes */
.mp-pending-files {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 0 2px 8px;
}
.mp-pending-chip {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  background: #f0f0ee;
  color: #3a3a3c;
  padding: 4px 8px;
  border-radius: 8px;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mp-pending-chip--error { background: #fff0f0; color: #c0392b; border: 1px solid #ffc5c5; }
.mp-pending-chip--ready { background: #f0f7ff; color: #7c9c9c; border: 1px solid #bfdbfe; }
.mp-pending-remove {
  background: none;
  border: none;
  color: #8e8e93;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 0;
  flex-shrink: 0;
}
.mp-pending-remove:hover { color: #3a3a3c; }
.mp-pending-spinner {
  display: inline-block;
  width: 10px;
  height: 10px;
  border: 1.5px solid #d1d1d6;
  border-top-color: #636366;
  border-radius: 50%;
  animation: mp-spin 0.7s linear infinite;
  flex-shrink: 0;
}
.mp-attach-btn {
  background: none;
  border: none;
  color: #aeaeb2;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  transition: color 0.15s;
}
.mp-attach-btn:hover:not(:disabled) { color: #636366; }
.mp-attach-btn:disabled { opacity: 0.4; cursor: default; }

/* Barre établissement lié au-dessus de l'input */
.mp-est-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #2d6a4f;
  background: #f0f7f4;
  border: 1px solid #b7e4c7;
  border-radius: 8px;
  padding: 5px 10px;
  margin-bottom: 6px;
}
.mp-est-bar__icon { flex-shrink: 0; }
.mp-est-bar__name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mp-est-bar__remove {
  background: none;
  border: none;
  color: #74c69d;
  cursor: pointer;
  font-size: 15px;
  line-height: 1;
  padding: 0;
  flex-shrink: 0;
  transition: color 0.12s;
}
.mp-est-bar__remove:hover { color: #2d6a4f; }

/* LLM @mention dropdown */
.mp-llm-dropdown {
  background: #ffffff;
  border: 1.5px solid #e8e8e6;
  border-radius: 10px;
  padding: 4px;
  margin-bottom: 6px;
  max-height: 220px;
  overflow-y: auto;
  box-shadow: 0 4px 16px rgba(0,0,0,0.08);
}
.mp-llm-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 7px 10px;
  border: none;
  background: none;
  border-radius: 7px;
  cursor: pointer;
  text-align: left;
  gap: 10px;
  transition: background 0.1s;
}
.mp-llm-option:hover,
.mp-llm-option--active { background: #f0f0ee; }
.mp-llm-option__title {
  font-size: 13px;
  font-weight: 500;
  color: #1c1c1e;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.mp-llm-option__provider {
  font-size: 11px;
  color: #aeaeb2;
  white-space: nowrap;
  flex-shrink: 0;
}

/* Badge LLM sélectionné */
.mp-pending-chip--llm {
  background: #f0f4ff;
  color: #7c9c9c;
  border: 1px solid #bfdbfe;
}
</style>
